/**
 * MegaMenu — desktop per-category navigation. Renders the domain triggers and,
 * on hover/click, one measured panel positioned under the active trigger and
 * clamped to the bar width (avoids right-edge overflow for wide panels).
 *
 * Hover intent: 60ms open delay + 160ms close delay; the panel wrapper's pt-2
 * padding bridges the trigger→panel gap. A click on a trigger opens its panel and
 * never closes it (see the onClick). A pointer-down outside-click closer
 * handles touch/hybrid devices where mouseleave is unreliable. Reads visibility
 * from getVisibleTree(user) — empty columns/domains never render.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getVisibleTree, NavDomain } from '../navigation/navTree';
import { NavAlerts } from './useNavAlerts';
import { Icon } from './navIcons';
import { NavDot } from './NavDot';
import useNavPrefsStore from '../stores/useNavPrefsStore';

interface MegaMenuProps {
  user: any;
  alerts: NavAlerts;
  /** Domain key to glow (e.g. hovered in the notification bell). */
  highlightKey?: string | null;
}

const PANEL_ID = 'megamenu-panel';

const FeaturedCard: React.FC<{ domain: NavDomain; onNavigate: () => void }> = ({ domain, onNavigate }) => {
  const f = domain.featured!;
  return (
    <Link
      to={f.to}
      onClick={onNavigate}
      className="group relative w-[220px] shrink-0 overflow-hidden rounded-xl border border-gray-700/50 bg-gradient-to-br from-[#2a2a2a] to-[#161616] p-3.5 flex flex-col gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
    >
      <Icon name={domain.icon} className="absolute -right-4 -bottom-4 w-24 h-24 text-primary opacity-[0.06]" />
      <div className="text-[9.5px] tracking-[0.16em] uppercase text-primary font-semibold">{f.eyebrow}</div>
      <div className="text-sm font-semibold text-white">{f.sub}</div>
      <div className="mt-auto flex items-center gap-2 text-[12px] text-primary font-medium">
        <span>{f.cta}</span>
        <span className="w-[22px] h-[22px] rounded-full bg-primary/[0.14] inline-flex items-center justify-center transition-transform group-hover:translate-x-0.5">
          <Icon name="arrow" className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
};

export const MegaMenu: React.FC<MegaMenuProps> = ({ user, alerts, highlightKey }) => {
  const location = useLocation();
  const tree = useMemo(() => getVisibleTree(user), [user]);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [panelLeft, setPanelLeft] = useState(0);
  const barRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const trigRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const bookmarks = useNavPrefsStore((s) => s.bookmarks);
  const toggleBookmark = useNavPrefsStore((s) => s.toggleBookmark);

  const isActive = (to: string) => location.pathname === to || location.pathname.startsWith(to + '/');
  const domainActive = (d: NavDomain) => d.columns.some((c) => c.items.some((i) => isActive(i.to)));

  const clearTimers = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  };
  const scheduleOpen = (key: string) => {
    clearTimers();
    openTimer.current = setTimeout(() => setOpenKey(key), 60);
  };
  const scheduleClose = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => setOpenKey(null), 160);
  };

  // close on navigation
  useEffect(() => setOpenKey(null), [location.pathname]);
  // close on Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && setOpenKey(null);
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);
  // close on outside pointer-down (touch/hybrid devices have unreliable mouseleave)
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (openKey && barRef.current && !barRef.current.contains(e.target as Node)) setOpenKey(null);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [openKey]);
  useEffect(() => () => clearTimers(), []);

  // position the open panel under its trigger, clamped to the bar
  useLayoutEffect(() => {
    if (!openKey || !barRef.current || !panelRef.current) return;
    const trig = trigRefs.current[openKey];
    if (!trig) return;
    const barRect = barRef.current.getBoundingClientRect();
    const trigRect = trig.getBoundingClientRect();
    const panelW = panelRef.current.offsetWidth;
    let left = trigRect.left - barRect.left;
    const max = barRect.width - panelW - 8;
    if (left > max) left = Math.max(8, max);
    if (left < 0) left = 0;
    setPanelLeft(left);
  }, [openKey]);

  const openDomain = tree.find((d) => d.key === openKey) || null;

  return (
    <div ref={barRef} className="relative hidden md:flex items-center gap-0.5">
      {tree.map((d) => {
        const active = domainActive(d);
        const isOpen = openKey === d.key;
        return (
          <button
            key={d.key}
            ref={(el) => {
              trigRefs.current[d.key] = el;
            }}
            onMouseEnter={() => scheduleOpen(d.key)}
            onMouseLeave={scheduleClose}
            onClick={() => {
              // Always opens, never toggles closed: hover has usually opened it a moment before the
              // click lands, and a toggle would shut it in the person's face.
              clearTimers();
              setOpenKey(d.key);
            }}
            aria-label={d.label}
            aria-haspopup="true"
            aria-expanded={isOpen}
            aria-controls={isOpen ? PANEL_ID : undefined}
            className={`relative inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[13.5px] font-medium transition-all duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${
              active
                ? 'text-primary'
                : isOpen
                ? 'text-white bg-white/5'
                : 'text-on-surface-variant hover:text-white hover:bg-white/[0.04]'
            } ${
              highlightKey === d.key
                ? 'ring-1 ring-primary/60 bg-primary/10 !text-primary shadow-[0_0_14px_-2px_rgba(255,204,51,0.55)]'
                : ''
            }`}
          >
            <Icon name={d.icon} className="w-[15px] h-[15px] opacity-90" />
            <span className="hidden lg:inline">{d.label}</span>
            <Icon
              name="chevron"
              strokeWidth={2.4}
              className={`w-3 h-3 opacity-60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>
        );
      })}

      {openDomain && (
        <div
          className="absolute top-full z-50 pt-2"
          style={{ left: panelLeft }}
          onMouseEnter={clearTimers}
          onMouseLeave={scheduleClose}
        >
          <div
            ref={panelRef}
            id={PANEL_ID}
            role="region"
            aria-label={`${openDomain.label} menu`}
            className="relative bg-[#242424] border border-gray-700/60 rounded-2xl shadow-2xl shadow-black/50 p-[18px]"
          >
            <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
            <div className="flex gap-[18px]">
              <div className="flex gap-6">
                {openDomain.columns.map((col) => (
                  <div key={col.title} className="min-w-[150px]">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-on-surface-variant font-semibold mb-2.5 pl-2.5">
                      {col.title}
                    </div>
                    {col.items.map((item) => {
                      const count = item.notif ? alerts[item.notif] || 0 : 0;
                      const pinned = bookmarks.includes(item.to);
                      return (
                        <div key={item.to} className="group relative rounded-[10px] hover:bg-[#323232] transition-colors">
                          <Link
                            to={item.to}
                            onClick={() => setOpenKey(null)}
                            className="flex items-start gap-2.5 px-2.5 py-2 pr-8 rounded-[10px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                          >
                            <Icon
                              name={item.icon || openDomain.icon}
                              className="w-4 h-4 mt-0.5 text-on-surface-variant group-hover:text-white shrink-0"
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 text-[13.5px] text-white font-medium">
                                {item.label}
                                {count > 0 && <NavDot count={count} />}
                                {item.lock && (
                                  <span className="text-[9px] tracking-wider uppercase text-on-surface-variant border border-gray-700 rounded px-1.5 py-px">
                                    Financial
                                  </span>
                                )}
                              </div>
                              {item.desc && <div className="text-[11.5px] text-on-surface-variant mt-px truncate max-w-[190px]">{item.desc}</div>}
                            </div>
                          </Link>
                          <button
                            type="button"
                            aria-label={pinned ? `Remove ${item.label} bookmark` : `Bookmark ${item.label}`}
                            aria-pressed={pinned}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleBookmark(item.to);
                            }}
                            className={`absolute top-2 right-2 p-0.5 rounded transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${
                              pinned
                                ? 'opacity-100 text-primary'
                                : 'opacity-0 group-hover:opacity-60 text-on-surface-variant hover:!opacity-100 hover:!text-primary'
                            }`}
                          >
                            <Icon name="star" className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              {openDomain.featured && <FeaturedCard domain={openDomain} onNavigate={() => setOpenKey(null)} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MegaMenu;
